import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { ArrowBack, Check, Close } from '@mui/icons-material';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type CropMode = '4:5' | '5:4' | 'freeform';

interface FullPageImageCropperProps {
  imageUrl: string;
  onConfirm: (cropData: CropArea, cropMode: CropMode) => void;
  onCancel: () => void;
  initialCropMode?: CropMode;
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'move';

const FullPageImageCropper: React.FC<FullPageImageCropperProps> = ({
  imageUrl,
  onConfirm,
  onCancel,
  initialCropMode = '4:5',
}) => {
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });
  const [cropMode, setCropMode] = useState<CropMode>(initialCropMode);
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandle, setDragHandle] = useState<ResizeHandle | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, cropX: 0, cropY: 0, cropWidth: 0, cropHeight: 0 });
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });
  const [imageDisplaySize, setImageDisplaySize] = useState({ width: 0, height: 0 });
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Constants for minimum crop size and aspect ratios
  const MIN_CROP_WIDTH = 512;
  const MIN_CROP_HEIGHT = 512;
  
  const ASPECT_RATIOS = {
    '4:5': 4 / 5,
    '5:4': 5 / 4,
    'freeform': null
  } as const;
  
  const currentAspectRatio = ASPECT_RATIOS[cropMode];

  // Calculate minimum display size based on natural image size
  const getMinDisplaySize = useCallback(() => {
    if (!imageNaturalSize.width || !imageDisplaySize.width) return { width: 160, height: 200 }; // 4:5 aspect ratio default
    
    const scale = imageDisplaySize.width / imageNaturalSize.width;
    return {
      width: MIN_CROP_WIDTH * scale,
      height: MIN_CROP_HEIGHT * scale,
    };
  }, [imageNaturalSize, imageDisplaySize]);

  // Initialize crop area when image loads
  const handleImageLoad = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const container = containerRef.current;
    
    setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    
    // Calculate display size to fit container while maintaining aspect ratio
    const containerRect = container.getBoundingClientRect();
    const availableWidth = containerRect.width - 40; // Account for padding
    const availableHeight = containerRect.height - 120; // Account for toolbar
    
    const imageAspectRatio = img.naturalWidth / img.naturalHeight;
    let displayWidth, displayHeight;
    
    if (availableWidth / availableHeight > imageAspectRatio) {
      // Container is wider than image aspect ratio
      displayHeight = availableHeight;
      displayWidth = displayHeight * imageAspectRatio;
    } else {
      // Container is taller than image aspect ratio
      displayWidth = availableWidth;
      displayHeight = displayWidth / imageAspectRatio;
    }
    
    setImageDisplaySize({ width: displayWidth, height: displayHeight });
    
    // Calculate image offset to center it
    const offsetX = (availableWidth - displayWidth) / 2 + 20;
    const offsetY = (availableHeight - displayHeight) / 2 + 60;
    setImageOffset({ x: offsetX, y: offsetY });
    
    // Initialize crop area (centered, using minimum size or 50% of image, whichever is larger)
    const scale = displayWidth / img.naturalWidth;
    const minDisplayWidth = MIN_CROP_WIDTH * scale;
    const minDisplayHeight = MIN_CROP_HEIGHT * scale;
    
    let cropWidth = Math.max(minDisplayWidth, displayWidth * 0.5);
    let cropHeight = Math.max(minDisplayHeight, displayHeight * 0.5);
    
    // Adjust for aspect ratio based on crop mode
    if (currentAspectRatio) {
      // For fixed aspect ratios, ensure the crop area matches
      if (cropWidth / cropHeight > currentAspectRatio) {
        cropWidth = cropHeight * currentAspectRatio;
      } else {
        cropHeight = cropWidth / currentAspectRatio;
      }
    }
    
    const initialCrop = {
      x: Math.max(0, (displayWidth - cropWidth) / 2),
      y: Math.max(0, (displayHeight - cropHeight) / 2),
      width: Math.min(cropWidth, displayWidth),
      height: Math.min(cropHeight, displayHeight),
    };

    setCropArea(initialCrop);
  }, [cropMode, currentAspectRatio]);

  // Handle resize window
  useEffect(() => {
    const handleResize = () => {
      if (imageRef.current) {
        handleImageLoad();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleImageLoad]);

  // Get cursor style for resize handles
  const getCursor = (handle: ResizeHandle): string => {
    switch (handle) {
      case 'nw': case 'se': return 'nw-resize';
      case 'ne': case 'sw': return 'ne-resize';
      case 'n': case 's': return 'n-resize';
      case 'e': case 'w': return 'e-resize';
      case 'move': return 'move';
      default: return 'default';
    }
  };

  // Start dragging
  const handleMouseDown = useCallback((e: React.MouseEvent, handle: ResizeHandle) => {
    e.preventDefault();
    setIsDragging(true);
    setDragHandle(handle);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      cropX: cropArea.x,
      cropY: cropArea.y,
      cropWidth: cropArea.width,
      cropHeight: cropArea.height,
    });
  }, [cropArea]);

  // Handle mouse move for dragging/resizing
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !dragHandle) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    const minSize = getMinDisplaySize();

    let newCrop = { ...cropArea };

    switch (dragHandle) {
      case 'move':
        newCrop.x = Math.max(0, Math.min(dragStart.cropX + deltaX, imageDisplaySize.width - cropArea.width));
        newCrop.y = Math.max(0, Math.min(dragStart.cropY + deltaY, imageDisplaySize.height - cropArea.height));
        break;

      case 'nw':
        const newWidth1 = Math.max(minSize.width, dragStart.cropWidth - deltaX);
        let newHeight1 = currentAspectRatio ? newWidth1 / currentAspectRatio : Math.max(minSize.height, dragStart.cropHeight - deltaY);
        newCrop.width = Math.min(newWidth1, dragStart.cropX + dragStart.cropWidth);
        newCrop.height = newHeight1;
        newCrop.x = dragStart.cropX + dragStart.cropWidth - newCrop.width;
        newCrop.y = dragStart.cropY + dragStart.cropHeight - newCrop.height;
        break;

      case 'ne':
        const newWidth2 = Math.max(minSize.width, dragStart.cropWidth + deltaX);
        let newHeight2 = currentAspectRatio ? newWidth2 / currentAspectRatio : Math.max(minSize.height, dragStart.cropHeight - deltaY);
        newCrop.width = Math.min(newWidth2, imageDisplaySize.width - dragStart.cropX);
        newCrop.height = newHeight2;
        newCrop.x = dragStart.cropX;
        newCrop.y = dragStart.cropY + dragStart.cropHeight - newCrop.height;
        break;

      case 'sw':
        const newWidth3 = Math.max(minSize.width, dragStart.cropWidth - deltaX);
        let newHeight3 = currentAspectRatio ? newWidth3 / currentAspectRatio : Math.max(minSize.height, dragStart.cropHeight + deltaY);
        newCrop.width = Math.min(newWidth3, dragStart.cropX + dragStart.cropWidth);
        newCrop.height = newHeight3;
        newCrop.x = dragStart.cropX + dragStart.cropWidth - newCrop.width;
        newCrop.y = dragStart.cropY;
        break;

      case 'se':
        const newWidth4 = Math.max(minSize.width, dragStart.cropWidth + deltaX);
        let newHeight4 = currentAspectRatio ? newWidth4 / currentAspectRatio : Math.max(minSize.height, dragStart.cropHeight + deltaY);
        newCrop.width = Math.min(newWidth4, imageDisplaySize.width - dragStart.cropX);
        newCrop.height = newHeight4;
        newCrop.x = dragStart.cropX;
        newCrop.y = dragStart.cropY;
        break;

      case 'n':
        const newHeight5 = Math.max(minSize.height, dragStart.cropHeight - deltaY);
        let newWidth5 = currentAspectRatio ? newHeight5 * currentAspectRatio : Math.max(minSize.width, dragStart.cropWidth);
        newCrop.height = newHeight5;
        newCrop.width = newWidth5;
        newCrop.y = dragStart.cropY + dragStart.cropHeight - newCrop.height;
        newCrop.x = dragStart.cropX + (dragStart.cropWidth - newCrop.width) / 2;
        break;

      case 's':
        const newHeight6 = Math.max(minSize.height, dragStart.cropHeight + deltaY);
        let newWidth6 = currentAspectRatio ? newHeight6 * currentAspectRatio : Math.max(minSize.width, dragStart.cropWidth);
        newCrop.height = Math.min(newHeight6, imageDisplaySize.height - dragStart.cropY);
        newCrop.width = newWidth6;
        newCrop.y = dragStart.cropY;
        newCrop.x = dragStart.cropX + (dragStart.cropWidth - newCrop.width) / 2;
        break;

      case 'e':
        const newWidth7 = Math.max(minSize.width, dragStart.cropWidth + deltaX);
        let newHeight7 = currentAspectRatio ? newWidth7 / currentAspectRatio : Math.max(minSize.height, dragStart.cropHeight);
        newCrop.width = Math.min(newWidth7, imageDisplaySize.width - dragStart.cropX);
        newCrop.height = newHeight7;
        newCrop.x = dragStart.cropX;
        newCrop.y = dragStart.cropY + (dragStart.cropHeight - newCrop.height) / 2;
        break;

      case 'w':
        const newWidth8 = Math.max(minSize.width, dragStart.cropWidth - deltaX);
        let newHeight8 = currentAspectRatio ? newWidth8 / currentAspectRatio : Math.max(minSize.height, dragStart.cropHeight);
        newCrop.width = Math.min(newWidth8, dragStart.cropX + dragStart.cropWidth);
        newCrop.height = newHeight8;
        newCrop.x = dragStart.cropX + dragStart.cropWidth - newCrop.width;
        newCrop.y = dragStart.cropY + (dragStart.cropHeight - newCrop.height) / 2;
        break;
    }

    // Ensure crop stays within image bounds
    newCrop.x = Math.max(0, Math.min(newCrop.x, imageDisplaySize.width - newCrop.width));
    newCrop.y = Math.max(0, Math.min(newCrop.y, imageDisplaySize.height - newCrop.height));

    setCropArea(newCrop);
  }, [isDragging, dragHandle, dragStart, cropArea, imageDisplaySize, getMinDisplaySize]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragHandle(null);
  }, []);

  // Add event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle confirm
  const handleConfirm = () => {
    if (!imageNaturalSize.width || !imageDisplaySize.width) return;
    
    // Convert display coordinates to natural image coordinates
    const scale = imageNaturalSize.width / imageDisplaySize.width;
    const finalCrop = {
      x: cropArea.x * scale,
      y: cropArea.y * scale,
      width: cropArea.width * scale,
      height: cropArea.height * scale,
    };
    
    onConfirm(finalCrop, cropMode);
  };

  // Calculate crop info
  const cropInfo = imageNaturalSize.width ? {
    width: Math.round(cropArea.width * imageNaturalSize.width / imageDisplaySize.width),
    height: Math.round(cropArea.height * imageNaturalSize.height / imageDisplaySize.height),
  } : null;

  return (
    <Box sx={{ 
      position: 'fixed', 
      paddingTop: '96px',
      paddingLeft: '16vw',
      paddingRight: '8vw',
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      backgroundColor: '#000',
      zIndex: 1300,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Top toolbar */}
      <AppBar position="static" sx={{ backgroundColor: '#333'}}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onCancel} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Crop Image
          </Typography>
          
          {/* Crop Mode Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <Typography variant="body2" sx={{ mr: 1 }}>Mode:</Typography>
            <ToggleButtonGroup
              value={cropMode}
              exclusive
              onChange={(_, newMode) => newMode && setCropMode(newMode)}
              size="small"
              sx={{ mr: 2 }}
            >
              <ToggleButton value="4:5" sx={{ color: 'white', '&.Mui-selected': { backgroundColor: 'primary.main' } }}>
                4:5
              </ToggleButton>
              <ToggleButton value="5:4" sx={{ color: 'white', '&.Mui-selected': { backgroundColor: 'primary.main' } }}>
                5:4
              </ToggleButton>
              <ToggleButton value="freeform" sx={{ color: 'white', '&.Mui-selected': { backgroundColor: 'primary.main' } }}>
                Free
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          {cropInfo && (
            <Typography variant="body2" sx={{ mr: 2 }}>
              {cropInfo.width} × {cropInfo.height} px
            </Typography>
          )}
          <Button
            color="inherit"
            startIcon={<Close />}
            onClick={onCancel}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<Check />}
            onClick={handleConfirm}
            disabled={!cropInfo}
          >
            Confirm
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main crop area */}
      <Box 
        ref={containerRef}
        sx={{ 
          flex: 1, 
          position: 'relative', 
          overflow: 'hidden',
          cursor: isDragging ? getCursor(dragHandle!) : 'default',
        }}
      >
        {/* Image */}
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Crop preview"
          onLoad={handleImageLoad}
          style={{
            position: 'absolute',
            left: imageOffset.x,
            top: imageOffset.y,
            width: imageDisplaySize.width,
            height: imageDisplaySize.height,
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />

        {/* Overlay */}
        {cropArea.width > 0 && (
          <>
            {/* Dark overlay */}
            <div
              style={{
                position: 'absolute',
                left: imageOffset.x,
                top: imageOffset.y,
                width: imageDisplaySize.width,
                height: imageDisplaySize.height,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none',
              }}
            />

            {/* Crop area */}
            <div
              style={{
                position: 'absolute',
                left: imageOffset.x + cropArea.x,
                top: imageOffset.y + cropArea.y,
                width: cropArea.width,
                height: cropArea.height,
                border: '2px solid #fff',
                backgroundColor: 'transparent',
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                cursor: 'move',
              }}
              onMouseDown={(e) => handleMouseDown(e, 'move')}
            >
              {/* Resize handles */}
              {['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map((handle) => (
                <div
                  key={handle}
                  style={{
                    position: 'absolute',
                    width: handle.length === 1 ? '8px' : '8px',
                    height: handle.length === 1 ? '8px' : '8px',
                    backgroundColor: '#fff',
                    border: '1px solid #000',
                    cursor: getCursor(handle as ResizeHandle),
                    ...getHandlePosition(handle),
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleMouseDown(e, handle as ResizeHandle);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </Box>
    </Box>
  );
};

// Helper function to position resize handles
const getHandlePosition = (handle: string) => {
  const offset = -4; // Half of handle size
  switch (handle) {
    case 'nw': return { top: offset, left: offset };
    case 'n': return { top: offset, left: '50%', transform: 'translateX(-50%)' };
    case 'ne': return { top: offset, right: offset };
    case 'e': return { top: '50%', right: offset, transform: 'translateY(-50%)' };
    case 'se': return { bottom: offset, right: offset };
    case 's': return { bottom: offset, left: '50%', transform: 'translateX(-50%)' };
    case 'sw': return { bottom: offset, left: offset };
    case 'w': return { top: '50%', left: offset, transform: 'translateY(-50%)' };
    default: return {};
  }
};

export default FullPageImageCropper;
